import { gql } from 'graphql-request'
import { z } from 'zod'
import { SearchForm } from '../components/SearchSideSheet/useSearchForm'
import { formatDateToShortFormat } from '../helpers/dateHelpers'
import { PassengersType } from '../helpers/searchQueryParams'
import { getVaaGQLClient } from './utils/vaaGQLClient'
import { flightFragment, flightSchema } from './models/flight'

const fareSegmentSchema = z.object({
  cabinName: z.string(),
})

const fareFamilyTypeProperty = z.enum(['AWARD-ECONOMY', 'AWARD-COMFORT-PLUS-PREMIUM-ECONOMY', 'AWARD-BUSINESS-FIRST'])

const availabilityProperty = z.enum(['SOLD_OUT', 'NOT_OFFERED']).nullable()

const fareSchema = z.object({
  id: z.string().nullable(),
  fareFamilyType: fareFamilyTypeProperty,
  availability: availabilityProperty,
  availableSeatCount: z.number().nullable(),
  price: z
    .object({
      awardPoints: z.string(),
    })
    .nullable(),
  fareSegments: z.array(fareSegmentSchema).nullable(),
})

const flightAndFaresSchema = z.object({
  flight: flightSchema,
  fares: z.array(fareSchema),
})

const flightSearchResponseSchema = z.object({
  searchOffers: z.object({
    result: z.object({
      slices: z.object({
        current: z.number(),
        total: z.number(),
      }),
      slice: z.object({
        flightsAndFares: z.array(flightAndFaresSchema),
      }),
      basketId: z.string(),
    }),
  }),
})

export type FareSegment = z.infer<typeof fareSegmentSchema>
export type Fare = z.infer<typeof fareSchema>
export type FlightAndFares = z.infer<typeof flightAndFaresSchema>
export type FlightSearchResponseType = z.infer<typeof flightSearchResponseSchema>
export type FareFamilyType = z.infer<typeof fareFamilyTypeProperty>
export type FareAvailabilityType = z.infer<typeof availabilityProperty>

const searchFlightsQueryDocument = gql`
  query SearchOffers($request: FlightOfferRequestInput!) {
    searchOffers(request: $request) {
      result {
        slices {
          current
          total
        }
        slice {
          flightsAndFares {
            flight {
              ...FlightFragment
            }
            fares {
              id
              fareFamilyType
              availability
              availableSeatCount
              price {
                awardPoints
              }
              fareSegments {
                cabinName
              }
            }
          }
        }
        basketId
      }
    }
  }

  ${flightFragment(true)}
`

const customerDetailsMapping: Record<keyof PassengersType, string> = {
  adults: 'ADT',
  youngAdults: 'GBE',
  children: 'CNN',
  infant: 'INF',
}

export const generateCustomerDetailsRequestParam = (passengers: PassengersType) => {
  return Object.entries(passengers).flatMap(([type, count]) => {
    const ptc = customerDetailsMapping[type as keyof PassengersType]

    return Array.from({ length: count }, (_, i) => ({
      custId: `${ptc}_${i}`,
      ptc,
    }))
  })
}

export async function getSearchFlights(form: SearchForm) {
  const { departure, destination, date, ...passengers } = form
  const customerDetails = generateCustomerDetailsRequestParam(passengers)

  const result = await getVaaGQLClient().request<FlightSearchResponseType>({
    document: searchFlightsQueryDocument,
    variables: {
      request: {
        currencyCode: 'GBP', // effects tax currency in the search summary
        flightSearchRequest: {
          awardSearch: true,
          searchOriginDestinations: [
            {
              origin: departure?.code,
              destination: destination?.code,
              departureDate: formatDateToShortFormat(date.from),
            },
          ],
          currentTripIndexId: '0',
        },
        customerDetails,
      },
    },
  })

  return flightSearchResponseSchema.parse(result)
}
